import { FC, ReactNode } from 'react';
import { classNames } from '../../../utils';
import { Loader } from '../../loader';
import { CustomClassContainer, CustomClassContainerProps } from '../../utility';

type ButtonThemeKey =
  | 'PRIMARY'
  | 'SECONDARY'
  | 'SMALL_PRIMARY'
  | 'SMALL_SECONDARY'
  | 'MINI_PRIMARY'
  | 'MINI_SECONDARY'
  | 'ALT';

export interface ButtonProps extends CustomClassContainerProps {
  theme: ButtonThemeKey;
  label: string;
  icon?: ReactNode;
  type?: 'submit' | 'reset' | 'button';
  tabIndex?: number;
  disabled?: boolean;
  isLoading?: boolean;
  onClick: () => void;
  className?: string;
  ltr?: boolean;
}

enum ButtonTheme {
  'PRIMARY' = 'px-6 py-4 border shadow-sm border-green bg-green hover:bg-green-dark hover:border-green-dark active:bg-green-dark text-white text-base',
  'ALT' = 'px-6 py-3 border shadow-sm text-xs hover:shadow-sm active:shadow-inner',
  'ALT_DISABLED' = 'px-6 py-3 border shadow-sm bg-gray-lightest border-gray-lightest text-gray-light text-xxs cursor-default',
  'SECONDARY' = 'px-6 py-4 border shadow-sm border-green bg-white hover:bg-green-lightest text-green text-base',
  'SMALL_PRIMARY' = 'px-4 py-2 border shadow-sm border-green bg-green hover:bg-green-dark hover:border-green-dark text-white text-sm',
  'SMALL_SECONDARY' = 'px-4 py-2 border shadow-sm border-green hover:bg-green text-green hover:text-white text-sm',
  'MINI_PRIMARY' = 'px-3 py-1 border shadow-sm border-green bg-green hover:bg-green-dark hover:border-green-dark text-white text-sm',
  'MINI_SECONDARY' = 'px-3 py-1 border shadow-sm border-green hover:bg-green text-green hover:text-white text-sm',
  'PRIMARY_DISABLED' = 'px-6 py-4 border shadow-sm border-gray-lightest bg-white text-base text-gray cursor-default',
  'SECONDARY_DISABLED' = 'px-6 py-4 border shadow-sm border-gray-lightest bg-white text-base text-gray cursor-default',
  'SMALL_PRIMARY_DISABLED' = 'px-4 py-2 border shadow-sm border-gray-lightest bg-white text-sm text-gray cursor-default',
  'SMALL_SECONDARY_DISABLED' = 'px-4 py-2 border shadow-sm border-gray-lightest bg-white text-sm text-gray cursor-default',
  'MINI_PRIMARY_DISABLED' = 'px-3 py-1 border shadow-sm border-gray-lightest bg-white text-sm text-gray cursor-default',
  'MINI_SECONDARY_DISABLED' = 'px-3 py-1 border shadow-sm border-gray-lightest bg-white text-sm text-gray cursor-default',
}

const Button: FC<ButtonProps> = ({
  label,
  onClick,
  theme,
  classes,
  disabled = false,
  icon,
  tabIndex,
  className,
  isLoading = false,
  type,
  ltr = true,
}) => {
  return (
    <CustomClassContainer classes={classes}>
      <button
        tabIndex={tabIndex}
        type={type}
        disabled={disabled || isLoading}
        className={classNames(
          'flex w-full flex-row items-center justify-center rounded-md font-bold uppercase outline-none focus:outline-none',
          disabled
            ? isLoading
              ? ButtonTheme[theme]
              : ButtonTheme[`${theme}_DISABLED`]
            : ButtonTheme[theme],
          className && !disabled ? className : '',
        )}
        onClick={() => onClick()}
      >
        {icon && ltr ? (
          !isLoading ? (
            <span className='mr-3'>{icon}</span>
          ) : null
        ) : null}
        {isLoading && (
          <span className='absolute flex flex-row items-center justify-center'>
            <Loader
              size='BUTTON'
              color={theme.includes('SECONDARY') ? 'GREEN' : 'WHITE'}
            />
          </span>
        )}
        <span className={classNames(isLoading && 'opacity-0')}>{label}</span>
        {icon && !ltr ? (
          !isLoading ? (
            <span className='ml-3'>{icon}</span>
          ) : null
        ) : null}
      </button>
    </CustomClassContainer>
  );
};

export default Button;
